<template>
  <section
    class="teams-integration-card"
    aria-labelledby="teams-integration-card-title"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <header class="teams-integration-card__header">
      <div class="teams-integration-card__logo">
        <v-icon size="28" color="#5059c9" aria-hidden="true">mdi-microsoft-teams</v-icon>
      </div>
      <div class="teams-integration-card__heading">
        <h3 id="teams-integration-card-title" class="teams-integration-card__title">
          Microsoft Teams
        </h3>
        <p class="teams-integration-card__description">{{ description }}</p>
      </div>
      <span
        class="teams-integration-card__badge"
        :class="`teams-integration-card__badge--${statusTone}`"
        data-test="teams-status-badge"
      >
        {{ statusLabel }}
      </span>
    </header>

    <ul v-if="status === 'not_connected'" class="teams-integration-card__benefits">
      <li v-for="benefit in benefits" :key="benefit" class="teams-integration-card__benefit">
        <svg
          class="teams-integration-card__check"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M16.667 5 7.5 14.167 3.333 10"
            stroke="#7f56d9"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>{{ benefit }}</span>
      </li>
    </ul>

    <div
      v-if="status === 'waiting_for_consent'"
      class="teams-integration-card__banner teams-integration-card__banner--info"
      role="note"
      data-test="teams-consent-banner"
    >
      <img :src="infoIconSrc" alt="" class="teams-integration-card__banner-icon" />
      <p class="teams-integration-card__banner-text">{{ consentBanner }}</p>
    </div>

    <div
      v-if="status === 'waiting_for_consent' && canManage"
      class="teams-integration-card__consent"
    >
      <p class="teams-integration-card__text">{{ consentHelp }}</p>
      <label class="teams-integration-card__sr-only" for="teams-consent-link"
        >Admin consent link</label
      >
      <input
        id="teams-consent-link"
        class="teams-integration-card__link-field"
        type="text"
        readonly
        :value="consentUrl"
        data-test="teams-consent-link"
        @focus="selectLink"
      />
      <p
        class="teams-integration-card__copy-status"
        :class="{ 'teams-integration-card__copy-status--error': copyFailed }"
        role="status"
        data-test="teams-copy-status"
      >
        <template v-if="copied">Link copied</template>
        <template v-else-if="copyFailed">{{ copyFailedMessage }}</template>
      </p>
    </div>

    <template v-if="status === 'rollout_pending'">
      <p class="teams-integration-card__text">{{ rolloutLead }}</p>
      <ol class="teams-integration-card__steps" data-test="teams-rollout-steps">
        <li
          class="teams-integration-card__step teams-integration-card__step--done"
          data-test="teams-step-1"
        >
          <v-icon size="20" color="#079455" aria-hidden="true">mdi-check-circle</v-icon>
          <div class="teams-integration-card__step-body">
            <span class="teams-integration-card__sr-only">Step 1, done: </span>
            <p class="teams-integration-card__step-title">Connect your Microsoft 365 tenant</p>
            <p v-if="connectedSummary" class="teams-integration-card__step-detail">
              {{ connectedSummary }}
            </p>
          </div>
        </li>
        <li class="teams-integration-card__step" data-test="teams-step-2">
          <v-icon size="20" color="#667085" aria-hidden="true">mdi-circle-outline</v-icon>
          <div class="teams-integration-card__step-body">
            <span class="teams-integration-card__sr-only">Step 2, to do: </span>
            <p class="teams-integration-card__step-title">
              Install the exIQtive app for your users
            </p>
            <p class="teams-integration-card__step-detail">{{ rolloutInstructions }}</p>
          </div>
        </li>
      </ol>
      <dl v-if="hasReachability" class="teams-integration-card__details">
        <div class="teams-integration-card__detail" data-test="teams-rollout-reach">
          <dt class="teams-integration-card__detail-label">Reachable</dt>
          <dd class="teams-integration-card__detail-value">
            {{ reachableCount }} of {{ totalCount }} employees so far
          </dd>
        </div>
      </dl>
    </template>

    <template v-if="status === 'connected' || status === 'needs_reconnect'">
      <div
        v-if="status === 'needs_reconnect'"
        class="teams-integration-card__banner teams-integration-card__banner--error"
        role="alert"
        data-test="teams-reconnect-banner"
      >
        <img :src="alertIconSrc" alt="" class="teams-integration-card__banner-icon" />
        <p class="teams-integration-card__banner-text">{{ reconnectMessage }}</p>
      </div>

      <dl v-if="detailRows.length" class="teams-integration-card__details">
        <div v-for="row in detailRows" :key="row.label" class="teams-integration-card__detail">
          <dt class="teams-integration-card__detail-label">{{ row.label }}</dt>
          <dd class="teams-integration-card__detail-value">{{ row.value }}</dd>
        </div>
      </dl>

      <div
        v-if="status === 'connected' && hasReachability"
        class="teams-integration-card__reach"
        data-test="teams-reach"
      >
        <p class="teams-integration-card__reach-title">
          {{ reachableCount }} of {{ totalCount }} employees reachable on Teams
        </p>
        <progress
          class="teams-integration-card__bar"
          max="100"
          :value="reachPercent"
          aria-label="Employees reachable on Teams"
          :aria-valuetext="`${reachableCount} of ${totalCount} employees reachable`"
          data-test="teams-reach-bar"
        />
        <p class="teams-integration-card__help" data-test="teams-reach-help">{{ reachHelp }}</p>
      </div>
    </template>

    <div v-if="canManage" class="teams-integration-card__actions">
      <StandardButton
        v-if="status === 'not_connected'"
        type="magenta"
        variant="primary"
        size="medium"
        label="Connect Microsoft Teams"
        :disabled="loading"
        :loader-left="loading"
        @click="emit('connect')"
      />
      <template v-else-if="status === 'waiting_for_consent'">
        <StandardButton
          type="magenta"
          variant="primary"
          size="medium"
          :label="copied ? 'Copied' : 'Copy link'"
          :disabled="loading || !consentUrl"
          @click="copyLink"
        />
        <StandardButton
          type="magenta"
          variant="outline"
          size="medium"
          label="Cancel"
          :disabled="loading"
          :loader-left="loading"
          @click="emit('cancel')"
        />
      </template>
      <template v-else-if="status === 'rollout_pending'">
        <a
          class="teams-integration-card__link-button"
          :href="teamsAdminCenterUrl"
          target="_blank"
          rel="noopener noreferrer"
          data-test="teams-open-admin-center"
          >Open Teams admin center<span class="teams-integration-card__sr-only">
            (opens in a new tab)</span
          ></a
        >
        <StandardButton
          type="magenta"
          variant="primary"
          size="medium"
          label="Mark step as done"
          :disabled="loading"
          :loader-left="loading"
          @click="emit('mark-rollout-done')"
        />
      </template>
      <StandardButton
        v-else-if="status === 'connected'"
        type="red"
        variant="outline"
        size="medium"
        label="Disconnect"
        :disabled="loading"
        :loader-left="loading"
        @click="emit('disconnect')"
      />
      <StandardButton
        v-else
        type="magenta"
        variant="primary"
        size="medium"
        label="Reconnect"
        :disabled="loading"
        :loader-left="loading"
        @click="emit('reconnect')"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const infoIconSrc = require("@components/assets/info-circle-brand.svg");
const alertIconSrc = require("@components/assets/alert-circle-error.svg");

const STATUS_CONFIG = {
  not_connected: { label: "Not connected", tone: "neutral" },
  waiting_for_consent: { label: "Waiting for admin consent", tone: "warning" },
  rollout_pending: { label: "Rollout pending", tone: "warning" },
  connected: { label: "Connected", tone: "success" },
  needs_reconnect: { label: "Needs reconnect", tone: "error" },
};

const benefits = [
  "Two one-time steps: a Microsoft 365 admin grants consent, then installs the exIQtive app for your users.",
  "Employees are matched by email address. Email always remains the fallback.",
];
const consentBanner = "A Microsoft 365 admin needs to approve exIQtive for your organization.";
const consentHelp =
  "Send this link to your Microsoft 365 admin (Global Admin or Teams admin). This page updates as soon as they approve.";
const copyFailedMessage = "Couldn't copy the link. Select it and copy it manually.";
const rolloutLead = "Finish setup so employees can receive Teams messages.";
const rolloutInstructions =
  "In the Teams admin center: Teams apps → Manage apps → exIQtive → Edit installs → choose all users or a group → Apply. Changes can take time to reach users.";
const reachHelp =
  "Employees who are not reachable have not had the app installed yet, or blocked it in Teams. They still receive email.";
const reconnectMessage =
  "exIQtive lost access to your Microsoft 365 tenant. Teams notifications are paused. Employees still receive email.";
const teamsAdminCenterUrl = "https://admin.teams.microsoft.com/policies/manage-apps";
const COPIED_RESET_MS = 2000;

const props = defineProps({
  /** Account-level Teams connection state from the integrations status API. */
  status: {
    type: String,
    default: "not_connected",
    validator: (value) =>
      [
        "not_connected",
        "waiting_for_consent",
        "rollout_pending",
        "connected",
        "needs_reconnect",
      ].includes(value),
  },
  /** Only the Account Owner / Co-Owner can connect, reconnect or disconnect. */
  canManage: {
    type: Boolean,
    default: false,
  },
  tenantName: {
    type: String,
    default: "",
  },
  /** Who connected, as returned by the status API: { name, email }. */
  connectedBy: {
    type: Object,
    default: null,
  },
  /** ISO date/time the tenant was connected. */
  connectedAt: {
    type: String,
    default: "",
  },
  /** Microsoft admin-consent URL shown while waiting for consent. */
  consentUrl: {
    type: String,
    default: "",
  },
  /** Employees with the exIQtive app installed; null while reachability is unknown. */
  reachableCount: {
    type: Number,
    default: null,
  },
  /** Employees in the tenant; null while reachability is unknown. */
  totalCount: {
    type: Number,
    default: null,
  },
  /** Disables the actions while a connect / cancel / rollout / disconnect request is in flight. */
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "connect",
  "cancel",
  "mark-rollout-done",
  "reconnect",
  "disconnect",
  "link-copied",
]);

const copied = ref(false);
const copyFailed = ref(false);
let copiedTimer = null;

const statusLabel = computed(() => STATUS_CONFIG[props.status].label);
const statusTone = computed(() => STATUS_CONFIG[props.status].tone);

const description = computed(() =>
  props.status === "connected"
    ? "Notifications are delivered as Teams direct messages."
    : "Send exIQtive notifications to your employees as Microsoft Teams direct messages."
);

const formattedConnectedAt = computed(() => {
  if (!props.connectedAt) return "";
  const date = new Date(props.connectedAt);
  if (Number.isNaN(date.getTime())) return props.connectedAt;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
});

const connectedByName = computed(() => props.connectedBy?.name || "");

const connectedSummary = computed(() => {
  const by = connectedByName.value ? `connected by ${connectedByName.value}` : "";
  const on = formattedConnectedAt.value ? `on ${formattedConnectedAt.value}` : "";
  return [props.tenantName, [by, on].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
});

const detailRows = computed(() => {
  if (props.status === "connected") {
    return [
      { label: "Tenant", value: props.tenantName },
      { label: "Connected by", value: connectedByName.value },
      { label: "Connected on", value: formattedConnectedAt.value },
    ].filter((row) => row.value);
  }
  if (props.status === "needs_reconnect") {
    const lastConnected = [
      formattedConnectedAt.value,
      connectedByName.value && `by ${connectedByName.value}`,
    ]
      .filter(Boolean)
      .join(" ");
    return [
      { label: "Tenant", value: props.tenantName },
      { label: "Last connected", value: lastConnected },
    ].filter((row) => row.value);
  }
  return [];
});

const hasReachability = computed(
  () => Number.isFinite(props.reachableCount) && Number.isFinite(props.totalCount)
);

const reachPercent = computed(() => {
  if (!hasReachability.value || props.totalCount <= 0) return 0;
  return Math.min(100, Math.round((props.reachableCount / props.totalCount) * 100));
});

function selectLink(event) {
  event.target.select();
}

async function copyLink() {
  clearTimeout(copiedTimer);
  try {
    await navigator.clipboard.writeText(props.consentUrl);
    copyFailed.value = false;
    copied.value = true;
    emit("link-copied");
    copiedTimer = setTimeout(() => {
      copied.value = false;
    }, COPIED_RESET_MS);
  } catch (error) {
    copied.value = false;
    copyFailed.value = true;
  }
}

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<style scoped>
.teams-integration-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  padding: 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #fff;
  font-family: Inter, sans-serif;
}

.teams-integration-card__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.teams-integration-card__logo {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 2px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fff;
}

.teams-integration-card__heading {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.teams-integration-card__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.teams-integration-card__description,
.teams-integration-card__text {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.teams-integration-card__badge {
  flex-shrink: 0;
  padding: 2px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.teams-integration-card__badge--neutral {
  background: #f9fafb;
  color: #344054;
}

.teams-integration-card__badge--warning {
  background: #fffaeb;
  color: #b54708;
}

.teams-integration-card__badge--success {
  background: #ecfdf3;
  color: #027a48;
}

.teams-integration-card__badge--error {
  background: #fee4e2;
  color: #b42318;
}

.teams-integration-card__benefits,
.teams-integration-card__steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.teams-integration-card__benefit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}

.teams-integration-card__check {
  flex-shrink: 0;
}

.teams-integration-card__banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
}

.teams-integration-card__banner--info {
  background: #f4ebff;
}

.teams-integration-card__banner--info .teams-integration-card__banner-text {
  color: #344054;
}

.teams-integration-card__banner--error {
  border: 1px solid #d92d20;
  background: #fee4e2;
}

.teams-integration-card__banner--error .teams-integration-card__banner-text {
  color: #7a271a;
}

.teams-integration-card__banner-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.teams-integration-card__banner-text {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.teams-integration-card__consent {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.teams-integration-card__link-field {
  box-sizing: border-box;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  font-family: inherit;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  text-overflow: ellipsis;
}

.teams-integration-card__link-field:focus-visible,
.teams-integration-card__link-button:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.teams-integration-card__copy-status {
  min-height: 20px;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #067647;
}

.teams-integration-card__copy-status--error {
  color: #b42318;
}

.teams-integration-card__step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.teams-integration-card__step-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.teams-integration-card__step-detail {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.teams-integration-card__details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.teams-integration-card__detail {
  display: flex;
  gap: 16px;
  font-size: 14px;
  line-height: 20px;
}

.teams-integration-card__detail-label {
  flex-shrink: 0;
  width: 140px;
  font-weight: 500;
  color: #475467;
}

.teams-integration-card__detail-value {
  margin: 0;
  font-weight: 400;
  color: #344054;
}

.teams-integration-card__reach {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.teams-integration-card__reach-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
}

.teams-integration-card__bar {
  appearance: none;
  display: block;
  width: 100%;
  height: 8px;
  border: 0;
  border-radius: 4px;
  background: #eaecf0;
  color: #7f56d9;
  overflow: hidden;
}

.teams-integration-card__bar::-webkit-progress-bar {
  border-radius: 4px;
  background: #eaecf0;
}

.teams-integration-card__bar::-webkit-progress-value {
  border-radius: 4px;
  background: #7f56d9;
}

.teams-integration-card__bar::-moz-progress-bar {
  border-radius: 4px;
  background: #7f56d9;
}

.teams-integration-card__help {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
}

.teams-integration-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.teams-integration-card__link-button {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 18px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: #344054;
  text-decoration: none;
  white-space: nowrap;
}

.teams-integration-card__link-button:hover {
  background: #f4f4f4;
}

.teams-integration-card__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
